import * as Sentry from "@sentry/react";

const REDACTED = "[REDACTED]";
const SENSITIVE_KEY =
  /(password|credential|cipher|authorization|token|email|login|share.?link|share.?url|delegate.?link)/i;

export const redactConnectionMetadata = (
  value: unknown,
  seen = new WeakSet<object>()
): unknown => {
  if (Array.isArray(value)) return value.map((item) => redactConnectionMetadata(item, seen));
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return "[Circular]";

  seen.add(value);
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
    (result, [key, entry]) => {
      result[key] = SENSITIVE_KEY.test(key)
        ? REDACTED
        : redactConnectionMetadata(entry, seen);
      return result;
    },
    {}
  );
};

const isDevelopment = import.meta.env.NODE_ENV !== "production";
type LogContext = Record<string, unknown>;

export const connectionHubLogger = {
  info: (message: string, context: LogContext = {}): void => {
    const sanitized = redactConnectionMetadata(context) as LogContext;
    if (isDevelopment) console.info("[ConnectionHub]", message, sanitized);
    Sentry.addBreadcrumb({ category: "connections", level: "info", message, data: sanitized });
  },
  error: (error: unknown, context: LogContext = {}): void => {
    const sanitized = redactConnectionMetadata(context) as LogContext;
    const message = error instanceof Error
      ? error.message
      : typeof error === "string" ? error : "ConnectionHub error";
    const capturedError = new Error(message);
    capturedError.name = "ConnectionHubError";
    const errorCode = typeof sanitized.errorCode === "string"
      ? sanitized.errorCode
      : "CONNECTION_UNKNOWN_ERROR";

    Sentry.withScope((scope) => {
      scope.setTag("feature", "connections");
      scope.setTag("errorCode", errorCode);
      scope.setContext("connectionHub", sanitized);
      Sentry.captureException(capturedError);
    });
  },
};
